import type { AvailabilityWindow } from 'src/redux/scheduling/types';

export const TIMELINE_START_HOUR = 7;
export const TIMELINE_HOURS = 11; // 7:00 – 18:00
export const TIMELINE_END_HOUR = TIMELINE_START_HOUR + TIMELINE_HOURS;
export const WEEK_DAYS_COUNT = 5;
export const WEEK_TOTAL_HOURS = WEEK_DAYS_COUNT * TIMELINE_HOURS; // 55

const padTwo = (num: number) => String(num).padStart(2, '0');

export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${padTwo(hours)}:${padTwo(mins)}`;
};

export const parseTimeToMinutes = (time: string): number => {
  const [hourStr, minuteStr = '0'] = time.trim().replace('.', ':').split(':');
  const hours = Number(hourStr), minutes = Number(minuteStr);
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : NaN;
};

// Availability windows wider than 16 hours are timezone-conversion artifacts
// (a UTC window crossing midnight can become an all-day span in local time).
// Exclude them so they don't force the timeline to render 0–24 h.
const MAX_WINDOW_MINUTES = 16 * 60;
// Windows starting before 4 AM local time are UTC-midnight artifacts
// (e.g. 06:00 UTC = 00:00 CDT). No real technician shift starts at midnight.
const MIN_START_MINUTES = 4 * 60;

export const computeTimelineBounds = (availabilityWindows: AvailabilityWindow[]): { startHour: number; hours: number } => {
  if (availabilityWindows.length === 0) {
    return { startHour: TIMELINE_START_HOUR, hours: TIMELINE_HOURS };
  }
  let minMinutes = Infinity;
  let maxMinutes = -Infinity;
  for (const window of availabilityWindows) {
    const start = parseTimeToMinutes(window.startTime);
    const end = parseTimeToMinutes(window.endTime);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    if (end - start > MAX_WINDOW_MINUTES) continue;
    if (start < MIN_START_MINUTES) continue;
    minMinutes = Math.min(minMinutes, start);
    maxMinutes = Math.max(maxMinutes, end);
  }
  if (!Number.isFinite(minMinutes) || !Number.isFinite(maxMinutes)) {
    return { startHour: TIMELINE_START_HOUR, hours: TIMELINE_HOURS };
  }
  const startHour = Math.max(0, Math.floor(minMinutes / 60) - 1);
  const endHour = Math.min(24, Math.ceil(maxMinutes / 60) + 1);
  return { startHour, hours: endHour - startHour };
};

// "YYYY-MM-DD" → 0=Mon … 4=Fri, -1 for weekends
export const getWeekDayIndex = (dateString: string): number => {
  if (!dateString) return -1;
  const [year, month, day] = dateString.split('-').map(Number);
  const dayOfWeek = new Date(year, month - 1, day).getDay();
  return dayOfWeek === 0 || dayOfWeek === 6 ? -1 : dayOfWeek - 1;
};

// Date → "YYYY-MM-DD" (local time)
export const toDateStr = (date: Date): string =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

// Monday of the week containing date
export const getWeekStart = (date: Date): Date => {
  const dayOfWeek = date.getDay(); // 0=Sun
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const start = new Date(date);
  start.setDate(date.getDate() + diff);
  return start;
};

// Monday and Friday of the week containing date — use instead of calling both separately
export const getWeekRange = (date: Date): [start: Date, end: Date] => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 4);
  return [start, end];
};

// Friday of the week containing date
export const getWeekEnd = (date: Date): Date => getWeekRange(date)[1];

// Shifts a YYYY-MM-DD date by `days`. Pinning to midday prevents DST midnight
// shifts from accidentally rolling the result forward or back a day.
// APX-569: the anchor and the arithmetic are UTC because the result is read back
// with toISOString(). Anchoring at midday LOCAL instead put the instant at 23:00
// UTC on the previous day east of UTC+12, so addDays(d, 1) === d and the callers
// that walk a range with it never terminated.
export const addDays = (dateStr: string, days: number): string => {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// Uses T12:00:00 to avoid DST-induced date shifts across all timezones.
export function isWeekend(dateStr: string): boolean {
  const dayOfWeek = new Date(`${dateStr}T12:00:00`).getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

export type WeekSegment = { segmentStart: string; segmentEnd: string };

// Groups an array of navigable dates into calendar-week segments.
// Each segment spans Monday through at least Friday; if the navigable dates
// include an active Saturday or Sunday for that week, segmentEnd is extended
// to cover them so day-view loops and week-view range checks include weekends.
export function getWeekSegmentsFromDates(dates: string[]): WeekSegment[] {
  const seenMondays = new Set<string>();
  const segments: WeekSegment[] = [];
  const datesSet = new Set(dates);
  for (const date of dates) {
    const monday = toDateStr(getWeekStart(new Date(`${date}T12:00:00`)));
    if (!seenMondays.has(monday)) {
      seenMondays.add(monday);
      const friday = toDateStr(getWeekEnd(new Date(`${date}T12:00:00`)));
      let segmentEnd = friday;
      const saturday = addDays(friday, 1);
      const sunday = addDays(friday, 2);
      if (datesSet.has(sunday)) segmentEnd = sunday;
      else if (datesSet.has(saturday)) segmentEnd = saturday;
      segments.push({ segmentStart: monday, segmentEnd });
    }
  }
  return segments;
}

export type LunchPlacementInput = {
  windowStartMinutes: number;
  windowEndMinutes: number;
  durationMinutes: number;
  jobs: Array<{ startMinutes: number; endMinutes: number }>;
  recommended?: { startMinutes: number; endMinutes: number };
};

type TimeRange = { startMinutes: number; endMinutes: number };

function overlapsRange(a: TimeRange, b: TimeRange): boolean {
  return a.startMinutes < b.endMinutes && a.endMinutes > b.startMinutes;
}

function findGapInWindow(
  windowStartMinutes: number,
  windowEndMinutes: number,
  durationMinutes: number,
  jobs: TimeRange[],
): TimeRange | null {
  const sortedJobs = jobs
    .filter((job) => overlapsRange(job, { startMinutes: windowStartMinutes, endMinutes: windowEndMinutes }))
    .sort((jobA, jobB) => jobA.startMinutes - jobB.startMinutes);

  let gapStart = windowStartMinutes;
  for (const job of sortedJobs) {
    const gapEnd = Math.min(job.startMinutes, windowEndMinutes);
    if (gapEnd - gapStart >= durationMinutes) {
      return { startMinutes: gapStart, endMinutes: gapStart + durationMinutes };
    }
    gapStart = Math.max(gapStart, job.endMinutes);
  }

  return null;
}

export function computeLunchPlacement(input: LunchPlacementInput): TimeRange {
  const { windowStartMinutes, windowEndMinutes, durationMinutes, jobs, recommended } = input;

  if (recommended) return recommended;

  const latestJobEnd = jobs.reduce((latest, job) => {
    const clippedEnd = Math.min(job.endMinutes, windowEndMinutes);
    const jobOverlapsWindow = clippedEnd > windowStartMinutes && job.startMinutes < windowEndMinutes;
    return jobOverlapsWindow ? Math.max(latest, clippedEnd) : latest;
  }, windowStartMinutes);

  const afterJobStart = latestJobEnd;
  const afterJobEnd = afterJobStart + durationMinutes;

  if (afterJobEnd <= windowEndMinutes) {
    return { startMinutes: afterJobStart, endMinutes: afterJobEnd };
  }

  // No space after latest job — shift back to window end and check for overlap
  const shiftedStart = Math.max(windowStartMinutes, windowEndMinutes - durationMinutes);
  const shiftedEnd = Math.min(shiftedStart + durationMinutes, windowEndMinutes);
  const shiftedRange: TimeRange = { startMinutes: shiftedStart, endMinutes: shiftedEnd };

  const shiftedOverlapsJob = jobs.some((job) => overlapsRange(shiftedRange, job));
  if (!shiftedOverlapsJob) {
    return shiftedRange;
  }

  // Shifted position conflicts — scan for the first fitting gap in the window
  return findGapInWindow(windowStartMinutes, windowEndMinutes, durationMinutes, jobs) ?? shiftedRange;
}
